import OptimizedImage from "@shared/ui/OptimizedImage"
import type { ScrollGalleryProps } from "@site-types/components/workScrollGallery"
import useEmblaCarousel from "embla-carousel-react"
import { useCallback, useEffect, useState } from "react"
import { ScrollGalleryModal } from "./ScrollGalleryModal"
export function ScrollGallery({ items }: ScrollGalleryProps) {
  const galleryItems = items.filter((item) => item.coverSrc)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const [viewportRef, emblaApi] = useEmblaCarousel({
    align: "start",
    breakpoints: {
      "(min-width: 600px)": { active: false },
    },
    containScroll: "trimSnaps",
    dragFree: false,
    loop: false,
  })

  const updateCarouselState = useCallback(() => {
    if (!emblaApi) return

    setSelectedIndex(emblaApi.selectedScrollSnap())
    setCanScrollPrev(emblaApi.canScrollPrev())
    setCanScrollNext(emblaApi.canScrollNext())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return

    updateCarouselState()
    emblaApi.on("select", updateCarouselState)
    emblaApi.on("settle", updateCarouselState)
    emblaApi.on("reInit", updateCarouselState)

    return () => {
      emblaApi.off("select", updateCarouselState)
      emblaApi.off("settle", updateCarouselState)
      emblaApi.off("reInit", updateCarouselState)
    }
  }, [emblaApi, updateCarouselState])

  const scrollPrev = () => {
    emblaApi?.scrollPrev()
  }

  const scrollNext = () => {
    emblaApi?.scrollNext()
  }

  const openItem = (index: number) => {
    setOpenIndex(index)
  }

  const closeItem = () => {
    setOpenIndex(null)
  }

  return (
    <div
      className="[--work-mobile-gallery-control-gap:0.5rem] [--work-mobile-gallery-gap:1.25rem] [--work-mobile-gallery-shadow-space:18px] [--work-mobile-gallery-slide-size:90%] [--work-mobile-gallery-tags-gap:1rem] max-sm:pb-[var(--work-mobile-gallery-tags-gap)]"
      data-id="scroll-gallery"
      id="scroll-gallery"
    >
      <div
        data-id="scroll-gallery-viewport"
        className="overflow-y-hidden overflow-x-scroll pb-[20px] [--slide-shadow-space:18px] [--slide-spacing:calc(3rem-var(--slide-shadow-space))] [scrollbar-color:var(--blue-50)_var(--blue-50)] [scrollbar-width:thin] max-sm:overflow-x-hidden max-sm:pb-0 max-sm:[scrollbar-width:none] max-sm:[&::-webkit-scrollbar]:hidden max-page:[&_img]:w-[75vw]"
        ref={viewportRef}
      >
        <ul
          data-id="scroll-gallery-container"
          className="ml-[calc(var(--slide-spacing)*-1)] flex list-none items-stretch [touch-action:pan-y_pinch-zoom] max-sm:ml-0 max-sm:gap-x-[var(--work-mobile-gallery-gap)] max-sm:pr-[var(--work-mobile-gallery-shadow-space)]"
          data-testid="scroll-gallery-modal"
        >
          {galleryItems.map((item, index) => (
            <li
              className="flex-[0_0_auto] list-none pr-[var(--slide-shadow-space)] pb-[var(--slide-shadow-space)] pl-[var(--slide-spacing)] max-sm:flex-[0_0_var(--work-mobile-gallery-slide-size)] max-sm:pr-0 max-sm:pb-[var(--work-mobile-gallery-shadow-space)] max-sm:pl-0"
              data-id={`scroll-gallery-item-${item.id}`}
              data-testid="scroll-gallery-slide"
              key={item.id}
            >
              <button
                className="block cursor-pointer border-4 border-[var(--white)] bg-[var(--green)] p-0 leading-[0] shadow-[10px_10px_0_var(--blue-50)] max-sm:min-h-[120px]"
                data-id={`scroll-gallery-open-${item.id}`}
                type="button"
                aria-label={`Open gallery: ${item.label || item.coverAlt || item.coverTitle || item.id}`}
                onClick={() => openItem(index)}
              >
                <OptimizedImage
                  className="block aspect-video h-full w-[30vw] max-w-[500px] object-cover max-sm:w-full max-sm:max-w-none"
                  src={item.coverSrc}
                  alt={item.coverAlt}
                  title={item.coverTitle}
                  {...item.coverDimensions}
                />
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div
        data-id="scroll-gallery-controls"
        className="mt-[var(--work-mobile-gallery-control-gap)] hidden items-center justify-center gap-[0.75rem] max-sm:flex"
        aria-label="Marketing gallery controls"
      >
        <button
          className="grid h-[38px] w-[38px] cursor-pointer place-items-center border-0 bg-[var(--white)] text-[0.9rem] text-[var(--deep-blue)] leading-none [clip-path:polygon(0_0,calc(100%-7.5px)_0,100%_7.5px,100%_100%,7.5px_100%,0_calc(100%-7.5px))] disabled:cursor-default disabled:opacity-[0.35]"
          data-id="scroll-gallery-prev"
          type="button"
          onClick={scrollPrev}
          disabled={!canScrollPrev}
          aria-label="Previous marketing item"
        >
          <i className="fa-sharp fa-solid fa-chevron-left" />
        </button>
        <div
          className="font-bold text-[1rem] text-[var(--white)] leading-none"
          data-id="marketing-gallery-status"
          data-testid="marketing-gallery-status"
          aria-live="polite"
        >
          {galleryItems.length > 0 ? selectedIndex + 1 : 0} / {galleryItems.length}
        </div>
        <button
          className="grid h-[38px] w-[38px] cursor-pointer place-items-center border-0 bg-[var(--white)] text-[0.9rem] text-[var(--deep-blue)] leading-none [clip-path:polygon(0_0,calc(100%-7.5px)_0,100%_7.5px,100%_100%,7.5px_100%,0_calc(100%-7.5px))] disabled:cursor-default disabled:opacity-[0.35]"
          data-id="scroll-gallery-next"
          type="button"
          onClick={scrollNext}
          disabled={!canScrollNext}
          aria-label="Next marketing item"
        >
          <i className="fa-sharp fa-solid fa-chevron-right" />
        </button>
      </div>
      <ScrollGalleryModal
        items={galleryItems}
        openIndex={openIndex}
        onOpenChange={(open) => {
          if (!open) closeItem()
        }}
      />
    </div>
  )
}

export default ScrollGallery
